<template>
    <div
        :class="classFaq"
        class="faq"
    >
        <div class="faq-wrapper">
            <div
                v-if="data.showNavigation"
                class="navigation"
            >
                <ul>
                    <li
                        v-for="(
                            section, navigationIndex
                        ) in data.sections.filter((s) => s.title)"
                        :key="`section-${navigationIndex}`"
                    >
                        <a :href="`#section-${navigationIndex}`">
                            {{ section.title }}
                        </a>
                    </li>
                </ul>
            </div>

            <div class="questions">
                <div
                    v-for="(section, sectionIndex) in data.sections"
                    :id="`section-${sectionIndex}`"
                    :key="`section-${sectionIndex}`"
                    class="section"
                >
                    <h2 v-if="section.title">
                        {{ section.title }}
                    </h2>

                    <div
                        v-for="(question, questionIndex) in section.questions"
                        :id="`section-${sectionIndex}-question-${questionIndex}`"
                        :key="`section-${sectionIndex}-question-${questionIndex}`"
                        :class="{
                            open: isQuestionOpen(
                                getKey(sectionIndex, questionIndex)
                            ),
                        }"
                        class="question"
                    >
                        <div
                            class="question-title"
                            @click="
                                toggleQuestion(
                                    getKey(sectionIndex, questionIndex)
                                )
                            "
                        >
                            <h3>{{ question.question }}</h3>
                        </div>
                        <div class="question-answer">
                            <ContentComponentItem
                                v-for="(component, key) in question.content"
                                :key="key"
                                :background="'WHITE'"
                                :data="component"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, type Ref, ref } from 'vue'
import type { ComponentFaqFragment } from '@/graphql/api'

import ContentComponentItem from '@/components/cms/component/ContentComponentItem.vue'

const props = defineProps<{
    data: ComponentFaqFragment
}>()

const classFaq = computed(() => ({
    'with-navigation': props.data.showNavigation,
}))

const openedQuestions: Ref<Record<string, boolean>> = ref({})

const getKey = (sectionIndex: number, questionIndex: number) =>
    `${sectionIndex}-${questionIndex}`

props.data.sections.forEach((section, sectionIndex) => {
    section.questions.forEach((question, questionIndex) => {
        openedQuestions.value[getKey(sectionIndex, questionIndex)] =
            question.expandedByDefault
    })
})

const isQuestionOpen = (key: string) => openedQuestions.value[key]

const toggleQuestion = (key: string) => {
    openedQuestions.value[key] = !openedQuestions.value[key]
}
</script>

<style lang="scss" scoped>
.faq {
    &.with-navigation {
        .faq-wrapper {
            width: 1360px;
            padding: 0 20px;
            justify-content: space-between;

            @include min-width(768) {
                padding: 0 40px;
            }

            @include min-width(1280) {
                padding: 0 160px 0 40px;
            }
        }
    }

    .faq-wrapper {
        width: 720px;
        max-width: 100%;
        margin: 0 auto;
        padding: 0 20px;
        display: flex;
        position: relative;
        align-items: flex-start;

        .navigation {
            width: 330px;
            max-width: 100%;
            font-size: 0.875rem;
            line-height: 1.2em;
            font-family: $effra-font-stack;
            letter-spacing: 0.025em;
            text-transform: uppercase;
            font-weight: 400;
            position: sticky;
            top: 130px;
            display: none;

            @include min-width(1024) {
                display: block;
            }

            ul {
                li {
                    margin: 0 0 15px;
                }
            }
        }

        .questions {
            width: 680px;
            max-width: 100%;

            .section {
                margin: 0 0 60px;

                @include min-width(1024) {
                    margin: 0 0 100px;
                }

                h2 {
                    font-size: 0.875rem;
                    line-height: 1.3em;
                    font-family: $effra-font-stack;
                    letter-spacing: 0.025em;
                    text-transform: uppercase;
                    font-weight: 400;
                    padding: 0 0 20px;
                    @include border-bottom;
                }

                .question {
                    @include border-bottom;

                    .question-answer {
                        display: none;
                        padding: 10px 0 0;

                        @include min-width(768) {
                            padding: 0;
                        }
                    }

                    &.open {
                        .question-title {
                            &::after {
                                content: $icon-angle-down;
                            }
                        }

                        .question-answer {
                            display: block;
                        }
                    }

                    .question-title {
                        display: flex;
                        align-items: center;
                        cursor: pointer;

                        &::after {
                            font-family: $icon-font;
                            content: $icon-angle-right;
                            width: 40px;
                            height: 40px;
                            line-height: 40px;
                            text-align: center;
                            font-size: 14px;
                            flex-shrink: 0;
                        }

                        h3 {
                            font-size: 1.1rem;
                            line-height: 1.2em;
                            padding: 20px 0;
                            width: 100%;

                            @include min-width(768) {
                                padding: 25px 0;
                            }

                            @include min-width(1024) {
                                font-size: 1.3rem;
                            }
                        }
                    }
                }
            }
        }
    }
}
</style>
